/**
 * Bareun.ai Provider for grammar checking
 */

import { BaseProvider } from './BaseProvider'
import type { CheckOptions, ProviderType, ProviderConfig } from './types'
import type { GrammarError } from '$lib/types/grammar'
import { GrammarErrorFactory } from '../GrammarErrorFactory'

interface BareunApiError {
  errorCode: string
  message: string
  startPos: number
  endPos: number
  orgStr: string
  candWord: string[]
  help?: string
}

interface BareunRevisionBlock {
  origin: {
    content: string
    beginOffset: number
    length: number
  }
  revised: string
  revisions: Array<{
    revised: string
    comment?: string
  }>
}

interface BareunRevisedSentence {
  origin: string
  revised: string
  revisedBlocks?: BareunRevisionBlock[]
}

interface BareunApiResponse {
  origin: string
  revised: string
  revisedSentences?: BareunRevisedSentence[]
  result?: {
    errors: BareunApiError[]
    original: string
    corrected: string
  }
  status: 'success' | 'error'
  message?: string
}

export class BareunProvider extends BaseProvider {
  constructor(config?: ProviderConfig) {
    super(config)
    if (!this.serverUrl) {
      this.serverUrl = 'https://api.bareun.ai'
    }
  }

  getName(): string {
    return 'Bareun.ai'
  }

  getDescription(): string {
    return 'Bareun.ai 한국어 맞춤법 검사 API 서비스입니다. API 키가 필요합니다.'
  }

  requiresApiKey(): boolean {
    return true
  }

  requiresServerUrl(): boolean {
    return true
  }

  getType(): ProviderType {
    return 'bareun'
  }

  async checkText(text: string, options?: CheckOptions): Promise<GrammarError[]> {
    if (!text || text.trim().length === 0) {
      return []
    }

    if (!this.apiKey) {
      throw new Error('Bareun API 키가 필요합니다')
    }

    if (!this.serverUrl) {
      throw new Error('Bareun 서버 URL이 필요합니다')
    }

    try {
      const url = `${this.serverUrl}/bareun/api/v1/correct-error`
      
      const requestBody = {
        document: {
          content: text,
          type: "PLAIN_TEXT"
        },
        encoding_type: "UTF8",
        auto_split: false
      }

      const response = await this.makeRequest(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': this.apiKey
        },
        body: JSON.stringify(requestBody)
      }, options?.signal)

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('API 키가 유효하지 않습니다')
        }
        if (response.status === 429) {
          throw new Error('API 호출 한도를 초과했습니다')
        }
        throw new Error(`Bareun API 오류: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()
      return this.parseApiResponse(data, text)
    } catch (error) {
      console.error('Bareun provider error:', error)
      throw error instanceof Error ? error : new Error('Bareun API 연결 실패')
    }
  }

  /**
   * Parse Bareun API response to GrammarError array
   */
  private parseApiResponse(data: any, originalText: string): GrammarError[] {
    const allErrors: GrammarError[] = []
    
    // Check for errors in result field
    if (data.result?.errors && Array.isArray(data.result.errors)) {
      const apiErrors = data.result.errors.map((error: BareunApiError, index: number) => 
        GrammarErrorFactory.fromBareunApiError(error, index)
      )
      allErrors.push(...apiErrors)
    }
    
    // Check for revised sentences
    if (data.revisedSentences && Array.isArray(data.revisedSentences)) {
      let currentOffset = 0
      
      for (const sentence of data.revisedSentences) {
        if (sentence.revisedBlocks) {
          for (const block of sentence.revisedBlocks) {
            const error = this.createErrorFromRevisionBlock(
              block, 
              currentOffset,
              allErrors.length
            )
            if (error) {
              allErrors.push(error)
            }
          }
        }
        currentOffset += sentence.origin.length
      }
    }
    
    // Fallback: compare origin and revised text
    if (allErrors.length === 0 && data.origin !== data.revised) {
      const diffs = this.findTextDifferences(data.origin, data.revised)
      for (let i = 0; i < diffs.length; i++) {
        const error = this.createErrorFromDiff(diffs[i], i)
        allErrors.push(error)
      }
    }
    
    // Remove duplicates and overlapping errors
    const uniqueErrors: GrammarError[] = []
    
    // Sort by position first
    const sortedErrors = [...allErrors].sort((a, b) => {
      const aStart = a.position?.start || 0
      const bStart = b.position?.start || 0
      return aStart - bStart
    })
    
    for (const error of sortedErrors) {
      const start = error.position?.start ?? 0
      const end = error.position?.end ?? 0
      const original = error.original || error.originalText || ''
      const suggestions = error.suggestions || []
      
      // Skip if original and suggestion are the same
      if (suggestions.length > 0 && original === suggestions[0]) {
        continue
      }
      
      // Check if this error overlaps with any existing error
      let shouldAdd = true
      for (let i = 0; i < uniqueErrors.length; i++) {
        const existing = uniqueErrors[i]
        const existingStart = existing.position?.start ?? 0
        const existingEnd = existing.position?.end ?? 0
        
        // Check for position overlap
        const overlaps = (start <= existingEnd && end >= existingStart) ||
                        (start >= existingStart && start <= existingEnd) ||
                        (end >= existingStart && end <= existingEnd)
        
        if (overlaps) {
          // Keep the one with larger range (more complete error)
          const errorRange = end === start ? 0 : end - start
          const existingRange = existingEnd === existingStart ? 0 : existingEnd - existingStart
          
          if (errorRange > existingRange) {
            // Replace existing with current
            uniqueErrors[i] = error
          }
          shouldAdd = false
          break
        }
      }
      
      if (shouldAdd) {
        uniqueErrors.push(error)
      }
    }
    
    // Return unique errors sorted by position
    return uniqueErrors.sort((a, b) => {
      const aStart = a.position?.start || 0
      const bStart = b.position?.start || 0
      return aStart - bStart
    })
  }

  /**
   * Create GrammarError from revision block
   */
  private createErrorFromRevisionBlock(
    block: BareunRevisionBlock, 
    sentenceOffset: number,
    index: number
  ): GrammarError | null {
    if (!block.origin || !block.revised) {
      return null
    }

    const startPos = sentenceOffset + block.origin.beginOffset
    const endPos = startPos + block.origin.length

    return {
      id: this.generateId(),
      type: 'spelling' as const,
      level: 'error' as const,
      original: block.origin.content,
      originalText: block.origin.content,
      position: {
        start: startPos,
        end: endPos
      },
      corrections: [{
        id: this.generateId(),
        text: block.revised
      }],
      message: block.revisions?.[0]?.comment || '맞춤법 오류',
      description: block.revisions?.[0]?.comment || '맞춤법 오류'
    }
  }

  /**
   * Find differences between original and revised text
   */
  private findTextDifferences(original: string, revised: string): Array<{
    original: string
    revised: string
    start: number
    end: number
  }> {
    const diffs: Array<{
      original: string
      revised: string
      start: number
      end: number
    }> = []
    
    // Simple diff algorithm - can be improved with better algorithm
    let i = 0
    let j = 0
    
    while (i < original.length && j < revised.length) {
      if (original[i] === revised[j]) {
        i++
        j++
      } else {
        // Find the extent of the difference
        const startPos = i
        let endPos = i + 1
        let revisedEnd = j + 1
        
        // Look ahead to find where texts align again
        while (endPos < original.length && revisedEnd < revised.length) {
          if (original.substring(endPos, endPos + 10) === 
              revised.substring(revisedEnd, revisedEnd + 10)) {
            break
          }
          endPos++
          revisedEnd++
        }
        
        diffs.push({
          original: original.substring(startPos, endPos),
          revised: revised.substring(j, revisedEnd),
          start: startPos,
          end: endPos
        })
        
        i = endPos
        j = revisedEnd
      }
    }
    
    return diffs
  }

  /**
   * Create GrammarError from text difference
   */
  private createErrorFromDiff(
    diff: { original: string; revised: string; start: number; end: number },
    index: number
  ): GrammarError {
    return {
      id: this.generateId(),
      type: 'spelling' as const,
      level: 'error' as const,
      original: diff.original,
      originalText: diff.original,
      position: {
        start: diff.start,
        end: diff.end
      },
      corrections: [{
        id: this.generateId(),
        text: diff.revised
      }],
      message: '맞춤법 오류',
      description: '맞춤법 오류'
    }
  }

  /**
   * Test connection to Bareun API
   */
  async testConnection(): Promise<{ success: boolean; message: string; responseTime?: number }> {
    if (!this.apiKey) {
      return {
        success: false,
        message: 'API 키가 설정되지 않았습니다'
      }
    }

    if (!this.serverUrl) {
      return {
        success: false,
        message: '서버 URL이 설정되지 않았습니다'
      }
    }

    const startTime = Date.now()
    
    try {
      // Try a simple health check first
      const healthUrl = `${this.serverUrl}/health`
      const healthResponse = await fetch(healthUrl, {
        method: 'GET',
        headers: this.apiKey ? { 'api-key': this.apiKey } : {}
      })

      if (healthResponse.ok) {
        return {
          success: true,
          message: '연결 성공',
          responseTime: Date.now() - startTime
        }
      }

      // If health check fails, try actual API
      await this.checkText('테스트')
      
      return {
        success: true,
        message: '연결 성공',
        responseTime: Date.now() - startTime
      }
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : '연결 실패',
        responseTime: Date.now() - startTime
      }
    }
  }
}